import {
   type ReactNode,
   type FormEvent,
   type RefObject,
   type ChangeEvent,
   type InputHTMLAttributes,
   useEffect,
} from 'react';
import { cn } from '@ifixit/ui/lib/utils';
import { useTranslations } from '@ifixit/i18n/hooks';

interface SearchInputProps {
   query: string;
   setQuery: (value: string) => void;
   clearSearch: () => void;
   placeholder: string;
   inputRef: RefObject<HTMLInputElement | null>;
   inputProps?: InputHTMLAttributes<HTMLInputElement>;
   inputAfter?: ReactNode;
   onSubmit: (e: FormEvent) => void;
   popoverLoading?: boolean;
}

/**
 * Search input component with magnifying glass and clear button
 */
export function SearchInput({
   query,
   setQuery,
   clearSearch,
   placeholder,
   inputRef,
   inputProps,
   inputAfter,
   onSubmit,
   popoverLoading = false,
}: SearchInputProps) {
   const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
   };

   const { className: inputClassName, ...restInputProps } = inputProps || {};
   const t = useTranslations('Search');

   useEffect(() => {
      if (query && inputRef.current && window.matchMedia('(max-width: 500px)').matches) {
         const rect = inputRef.current.getBoundingClientRect();
         window.scrollBy({ top: rect.top - 25, behavior: 'smooth' });
      }
   }, [query, inputRef]);

   return (
      <form onSubmit={onSubmit} className="w-full" onClick={e => e.stopPropagation()}>
         <div className="relative flex justify-between w-full">
            <div className="absolute flex left-0 top-0 bottom-0 w-10 pointer-events-none">
               {popoverLoading ? (
                  <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full m-auto" />
               ) : (
                  <SearchIcon />
               )}
            </div>
            <input
               ref={inputRef}
               className={cn(
                  'instant-search-input w-full px-10 py-2 bg-gray-200 text-gray-950 placeholder:text-gray-600 outline-none focus:ring-2 focus:ring-brand-500 border-none !text-[16px]',
                  inputClassName
               )}
               placeholder={placeholder || t('input.placeholder')}
               value={query}
               onChange={handleChange}
               aria-label={t('input.ariaLabel')}
               spellCheck="false"
               autoComplete="off"
               {...restInputProps}
            />
            {!query && inputAfter}
            {query && (
               <div className="absolute flex right-0 top-0 bottom-0 w-10">
                  {
                     <button
                        onClick={e => {
                           e.preventDefault();
                           e.stopPropagation();
                           clearSearch();
                        }}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none flex"
                        aria-label={t('actions.clear')}
                        data-testid="clear-search"
                        type="button"
                     >
                        <CloseIcon />
                     </button>
                  }
               </div>
            )}
         </div>
      </form>
   );
}

function CloseIcon() {
   return (
      <div className="size-4 m-auto flex items-center justify-center">
         <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 320 512"
            className="w-4 h-4 text-gray-500 hover:text-gray-700"
            aria-hidden="true"
            focusable="false"
         >
            <path
               fill="currentColor"
               d="M310.6 361.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L160 301.3l-105.4 105.4c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L114.7 256 9.4 150.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 210.7l105.4-105.4c12.5-12.5 32.8-12.5 45.3 0s12.5 32.8 0 45.3L205.3 256l105.3 105.4z"
            />
         </svg>
      </div>
   );
}

function SearchIcon() {
   return (
      <div className="size-4 m-auto flex items-center justify-center" data-testid="search-icon">
         <svg
            className="search-icon w-4 h-4 text-gray-600"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
         >
            <circle cx="11" cy="11" r="7" stroke="currentColor" />
            <line
               x1="16.65"
               y1="16.65"
               x2="21"
               y2="21"
               stroke="currentColor"
               strokeLinecap="round"
            />
         </svg>
      </div>
   );
}
